import {
  DEFAULT_DHL_PICKUP_COSTS,
  DHL_PICKUP_COSTS_BY_DC,
} from "../fulfillments/dhl-pickup-costs";
import {
  carrierAndServiceToServiceLevel,
  Carriers,
  CarrierSchema,
  DhlEcsDistributionCenter,
  ServiceLevel,
  ServiceSchema,
} from "../fulfillments/fulfillment-carriers-and-services";
import type { RedoCarrierRateTables } from "../rate-table/global-rate-tables";
import {
  OutboundWeightByZoneRateTable,
  WeightUnit,
} from "../rate-table/rate-table";
import { RateTableCarrier } from "../rate-table/rate-table-mapping";
import { mode } from "./rate-stats";

export const OZ_PER_LB = 16;
export const WEEKDAYS_PER_WEEK = 5;
export const WEEKDAYS_PER_YEAR = 260;

export const AMAZON_FUEL_SURCHARGE = 0.0875;

export function countWeekdays(startDate: Date, endDate: Date): number {
  let count = 0;
  const current = new Date(startDate);
  while (current <= endDate) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return Math.max(count, 1);
}

export function estimateWeekdays(calendarDays: number): number {
  return Math.max(Math.round(calendarDays * (WEEKDAYS_PER_WEEK / 7)), 1);
}

export const RATE_CALC_CARRIERS = [
  RateTableCarrier.DHL_ECS,
  RateTableCarrier.Amazon,
  RateTableCarrier.UPS,
  RateTableCarrier.USPS,
  RateTableCarrier.UNIUNI,
] as const;

export const REV_SHARE_CARRIERS = [
  RateTableCarrier.UPS,
  RateTableCarrier.USPS,
] as const;

export const SPREAD_CARRIERS = [
  RateTableCarrier.DHL_ECS,
  RateTableCarrier.Amazon,
  RateTableCarrier.UNIUNI,
] as const;

export enum UpsRateType {
  GroundSaver = "groundSaver",
  OtherServices = "otherServices",
}

export interface UspsRevShareSettings {
  underOneLbPct: number;
  overOneLbPct: number;
}

export interface UpsRevShareSettings {
  activeRateType: UpsRateType;
  groundSaverPct: number;
  otherServicesPct: number;
}

export interface RevShareSettings {
  usps: UspsRevShareSettings;
  ups: UpsRevShareSettings;
}

export const DEFAULT_REV_SHARE_SETTINGS: RevShareSettings = {
  usps: { underOneLbPct: 1, overOneLbPct: 4 },
  ups: {
    activeRateType: UpsRateType.GroundSaver,
    groundSaverPct: 5,
    otherServicesPct: 11,
  },
};

export function getRevSharePercentage(
  carrier: RateTableCarrier,
  weightOz: number,
  settings: RevShareSettings,
): number {
  if (carrier === RateTableCarrier.USPS) {
    return weightOz < OZ_PER_LB
      ? settings.usps.underOneLbPct / 100
      : settings.usps.overOneLbPct / 100;
  }
  if (carrier === RateTableCarrier.UPS) {
    return settings.ups.activeRateType === UpsRateType.GroundSaver
      ? settings.ups.groundSaverPct / 100
      : settings.ups.otherServicesPct / 100;
  }
  return 0;
}

export function classifyServiceLevel(
  service: string | null,
  carrier?: string | null,
): ServiceLevel {
  if (!service) return ServiceLevel.STANDARD;

  const parsedService = ServiceSchema.safeParse(service);
  if (parsedService.success && carrier) {
    const parsedCarrier = CarrierSchema.safeParse(carrier);
    if (parsedCarrier.success) {
      return (
        carrierAndServiceToServiceLevel(
          parsedCarrier.data as Carriers,
          parsedService.data as Parameters<
            typeof carrierAndServiceToServiceLevel
          >[1],
        ) ?? ServiceLevel.STANDARD
      );
    }
  }

  const s = service.toLowerCase();
  if (s.includes("express") || s.includes("overnight")) {
    return ServiceLevel.EXPRESS;
  }
  if (s.includes("priority") || s.includes("2 day")) {
    return ServiceLevel.PLUS;
  }
  return ServiceLevel.STANDARD;
}

export enum PricingStrategy {
  Spread = "spread",
  Discount = "discount",
}

export enum SpreadUnit {
  Dollars = "dollars",
  Percent = "percent",
}

export function ozToLb(weightOz: number): number {
  return weightOz / OZ_PER_LB;
}

export function extractWeightBucketsOz(
  rateTables: RedoCarrierRateTables,
): number[] {
  const allTableArrays = Object.values(rateTables.rateTables ?? {});
  for (const tables of allTableArrays) {
    if (!tables || tables.length === 0) continue;
    const table = tables[0]!;
    const yAxis = table.yAxis;
    if (yAxis.values.length === 0) continue;
    const multiplier = yAxis.units === WeightUnit.LB ? OZ_PER_LB : 1;
    return yAxis.values.map((b) => b.maxWeight * multiplier);
  }
  return [1, 2, 4, 8, 16, 32, 64, 128, 256];
}

export function lookupRateInTable(
  table: OutboundWeightByZoneRateTable,
  weightOz: number,
  zone: string,
): number | null {
  const weightLb =
    table.yAxis.units === WeightUnit.LB ? ozToLb(weightOz) : weightOz;

  const weightIdx = table.yAxis.values.findIndex(
    (bucket) => weightLb > bucket.minWeight && weightLb <= bucket.maxWeight,
  );
  if (weightIdx < 0) return null;

  const zoneIdx = table.xAxis.values.indexOf(zone);
  if (zoneIdx < 0) return null;

  const row = table.data[weightIdx];
  if (!row) return null;

  const value = row[zoneIdx];
  if (value === undefined || value === null) return null;

  const rate = parseFloat(value);
  return isNaN(rate) ? null : rate;
}

export function lookupRateByIndex(
  table: OutboundWeightByZoneRateTable,
  weightBucketIdx: number,
  zoneIdx: number,
): number | null {
  const row = table.data[weightBucketIdx];
  if (!row) return null;
  const val = row[zoneIdx];
  if (val === undefined || val === null) return null;
  const rate = parseFloat(val);
  return isNaN(rate) ? null : rate;
}

export function getCarrierRate(
  rateTables: RedoCarrierRateTables | null,
  carrier: RateTableCarrier,
  weightOz: number,
  zone: string,
  dcId?: string,
): number | null {
  const carrierTables =
    rateTables?.rateTables?.[carrier as keyof typeof rateTables.rateTables];
  if (!carrierTables || carrierTables.length === 0) return null;

  const table =
    carrier === RateTableCarrier.DHL_ECS && dcId
      ? carrierTables.find((t) => t.distributionCenterId === dcId)
      : carrierTables[0];

  if (!table) return null;
  const rate = lookupRateInTable(table, weightOz, zone);
  if (rate !== null && carrier === RateTableCarrier.Amazon) {
    return rate * (1 + AMAZON_FUEL_SURCHARGE);
  }
  return rate;
}

export function calculateDhlPickupCostPerPackage(
  annualPickupCost: number,
  dailyVolume: number,
  weeklyPickups: number,
): number {
  if (dailyVolume <= 0 || weeklyPickups <= 0) return 0;
  const annualPackages = dailyVolume * WEEKDAYS_PER_YEAR;
  return annualPickupCost / annualPackages;
}

export function getDhlPickupCostPerPickup(
  dcId: DhlEcsDistributionCenter,
  distanceMiles: number,
  needsGL: boolean,
): number {
  const costs = DHL_PICKUP_COSTS_BY_DC[dcId] ?? DEFAULT_DHL_PICKUP_COSTS;
  const isNear = distanceMiles <= 50;

  if (needsGL) {
    return isNear ? costs.straightTruckNear : costs.straightTruckFar;
  }
  return isNear ? costs.cargoVanNear : costs.cargoVanFar;
}

export function getDhlAnnualPickupCost(
  dcId: DhlEcsDistributionCenter,
  distanceMiles: number,
  needsGL: boolean,
  weeklyPickups: number,
): number {
  const perPickupCost = getDhlPickupCostPerPickup(dcId, distanceMiles, needsGL);
  return perPickupCost * weeklyPickups * 52;
}

export function calculateDiscountQuote(
  merchantRate: number,
  discountPct: number,
  baseRate: number,
): number {
  const discounted = merchantRate * (1 - discountPct / 100);
  return Math.max(discounted, baseRate);
}

export function calculateSpreadQuote(
  baseRate: number,
  spreadAmount: number,
): number {
  return baseRate + spreadAmount;
}

export interface HistoricalShipment {
  weightOz: number;
  zone: string;
  merchantPaid: number;
  currentCarrier?: string;
  excludeFromPricing?: boolean;
}

export interface RateGrid {
  weightBucketsOz: number[];
  zones: string[];
  priceGrid: (number | null)[][];
  countGrid: number[][];
}

export function buildRateGridFromShipments(
  shipments: HistoricalShipment[],
  weightBuckets: number[],
): RateGrid {
  const zonesSet = new Set<string>();
  for (const s of shipments) {
    zonesSet.add(s.zone);
  }
  const zones = Array.from(zonesSet).sort();

  const groups = new Map<string, { pricingPrices: number[]; count: number }>();

  for (const shipment of shipments) {
    const wi = weightBuckets.findIndex((bucket) => shipment.weightOz <= bucket);
    if (wi < 0) continue;

    const zi = zones.indexOf(shipment.zone);
    if (zi < 0) continue;

    const key = `${wi}|${zi}`;
    if (!groups.has(key)) groups.set(key, { pricingPrices: [], count: 0 });
    const group = groups.get(key)!;
    group.count++;
    if (!shipment.excludeFromPricing) {
      group.pricingPrices.push(shipment.merchantPaid);
    }
  }

  const priceGrid: (number | null)[][] = weightBuckets.map(() =>
    zones.map(() => null),
  );
  const countGrid: number[][] = weightBuckets.map(() => zones.map(() => 0));

  for (const [key, { pricingPrices, count }] of groups) {
    const parts = key.split("|").map(Number);
    const wi = parts[0]!;
    const zi = parts[1]!;
    priceGrid[wi]![zi] = pricingPrices.length > 0 ? mode(pricingPrices) : null;
    countGrid[wi]![zi] = count;
  }

  return { weightBucketsOz: weightBuckets, zones, priceGrid, countGrid };
}

export function determinePredominantCarrier(shipments: HistoricalShipment[]): {
  carrier: RateTableCarrier | null;
  pct: number;
} {
  const carrierCounts = new Map<string, number>();
  let totalWithCarrier = 0;

  for (const shipment of shipments) {
    if (shipment.currentCarrier) {
      const normalized = shipment.currentCarrier.toLowerCase();
      carrierCounts.set(normalized, (carrierCounts.get(normalized) || 0) + 1);
      totalWithCarrier++;
    }
  }

  if (totalWithCarrier === 0) {
    return { carrier: null, pct: 0 };
  }

  let maxCount = 0;
  let predominantCarrierStr: string | null = null;

  for (const [carrier, count] of carrierCounts) {
    if (count > maxCount) {
      maxCount = count;
      predominantCarrierStr = carrier;
    }
  }

  const pct = totalWithCarrier > 0 ? (maxCount / totalWithCarrier) * 100 : 0;

  let carrier: RateTableCarrier | null = null;
  if (predominantCarrierStr) {
    if (predominantCarrierStr.includes("ups")) {
      carrier = RateTableCarrier.UPS;
    } else if (predominantCarrierStr.includes("dhl")) {
      carrier = RateTableCarrier.DHL_ECS;
    } else if (predominantCarrierStr.includes("amazon")) {
      carrier = RateTableCarrier.Amazon;
    } else if (predominantCarrierStr.includes("uniuni")) {
      carrier = RateTableCarrier.UNIUNI;
    }
  }

  return { carrier, pct };
}

export interface CarrierEligibilityEntry {
  eligible: boolean;
  nearestDC?: string;
  distanceMiles?: number;
}

export type FullCarrierEligibility = Partial<
  Record<RateTableCarrier, CarrierEligibilityEntry>
>;

export function getCarrierRateWithEligibility(
  rateTables: RedoCarrierRateTables | null,
  weightOz: number,
  zone: string,
  carrier: RateTableCarrier,
  carrierEligibility: FullCarrierEligibility | null,
): number | null {
  if (!carrierEligibility) return null;
  const entry = carrierEligibility[carrier];
  if (!entry?.eligible) return null;
  return getCarrierRate(rateTables, carrier, weightOz, zone, entry.nearestDC);
}

export interface ValidCarrierRate<
  T extends RateTableCarrier = RateTableCarrier,
> {
  carrier: T;
  rate: number;
}

export function getBestCarrierRate<T extends RateTableCarrier>(
  rateTables: RedoCarrierRateTables | null,
  weightOz: number,
  zone: string,
  selectedCarriers: readonly T[],
  carrierEligibility: FullCarrierEligibility | null,
): ValidCarrierRate<T> | null {
  const rates: ValidCarrierRate<T>[] = [];

  for (const carrier of selectedCarriers) {
    const rate = getCarrierRateWithEligibility(
      rateTables,
      weightOz,
      zone,
      carrier,
      carrierEligibility,
    );
    if (rate !== null) {
      rates.push({ carrier, rate });
    }
  }

  if (rates.length === 0) return null;

  return rates.reduce((best, curr) => (curr.rate < best.rate ? curr : best));
}

export interface DhlSettings {
  needsGL: boolean;
  weeklyPickups: number;
}

export interface OptimizerConfig {
  dailyVolume: number;
  dhlSettings: DhlSettings;
}

export interface CarrierQuoteResult {
  carrier: RateTableCarrier;
  strategy: PricingStrategy;
  amount: number;
  unit?: SpreadUnit;
  ordersWon: number;
  ordersWonPct: number;
  totalMerchantSavings: number;
  totalRedoProfit: number;
  avgMerchantSavingsPerOrder: number;
  avgRedoProfitPerOrder: number;
  monthlySavings: number;
  annualSavings: number;
  annualRedoProfit: number;
}

export interface OptimizerResult {
  bestCarrier: RateTableCarrier;
  bestCarrierConfig: {
    strategy: PricingStrategy;
    amount: number;
    unit?: SpreadUnit;
  };
  projectedMonthlySavings: number;
  projectedAnnualSavings: number;
  projectedAnnualRedoProfit: number;
  ordersWonPct: number;
  allCarrierResults: CarrierQuoteResult[];
  predominantCarrier?: RateTableCarrier;
  predominantCarrierPct: number;
}

function getBestCarrierRateWithPickup(
  rateTables: RedoCarrierRateTables,
  weightOz: number,
  zone: string,
  selectedCarriers: RateTableCarrier[],
  carrierEligibility: FullCarrierEligibility,
  dhlPickupCost: number,
): ValidCarrierRate | null {
  const rates: ValidCarrierRate[] = [];

  for (const carrier of selectedCarriers) {
    const entry = carrierEligibility[carrier];
    if (!entry?.eligible) continue;
    const baseRate = getCarrierRate(
      rateTables,
      carrier,
      weightOz,
      zone,
      entry.nearestDC,
    );
    if (baseRate !== null) {
      const pickupAddon =
        carrier === RateTableCarrier.DHL_ECS ? dhlPickupCost : 0;
      rates.push({ carrier, rate: baseRate + pickupAddon });
    }
  }

  if (rates.length === 0) return null;

  const first = rates[0];
  if (!first) return null;

  return rates.reduce(
    (best, curr) => (curr.rate < best.rate ? curr : best),
    first,
  );
}

function calculateQuoteForCarrier(
  rateTables: RedoCarrierRateTables,
  rateGrid: RateGrid,
  carrier: RateTableCarrier,
  carrierEligibility: FullCarrierEligibility,
  strategy: PricingStrategy,
  amount: number,
  config: OptimizerConfig,
): CarrierQuoteResult | null {
  const dhlEntry =
    carrier === RateTableCarrier.DHL_ECS
      ? carrierEligibility[RateTableCarrier.DHL_ECS]
      : undefined;
  const dhlPickupCost =
    dhlEntry?.eligible && dhlEntry.nearestDC && dhlEntry.distanceMiles != null
      ? calculateDhlPickupCostPerPackage(
          getDhlAnnualPickupCost(
            dhlEntry.nearestDC as DhlEcsDistributionCenter,
            dhlEntry.distanceMiles,
            config.dhlSettings.needsGL,
            config.dhlSettings.weeklyPickups,
          ),
          config.dailyVolume,
          config.dhlSettings.weeklyPickups,
        )
      : 0;

  let totalOrders = 0;
  let ordersWon = 0;
  let totalMerchantSavings = 0;
  let totalRedoProfit = 0;

  for (let wi = 0; wi < rateGrid.weightBucketsOz.length; wi++) {
    const weightOz = rateGrid.weightBucketsOz[wi]!;

    for (let zi = 0; zi < rateGrid.zones.length; zi++) {
      const zone = rateGrid.zones[zi]!;
      const merchantRate = rateGrid.priceGrid[wi]?.[zi] ?? null;
      const orderCount = rateGrid.countGrid[wi]?.[zi] ?? 0;

      if (merchantRate === null || orderCount === 0) {
        continue;
      }

      totalOrders += orderCount;

      const bestCarrier = getBestCarrierRateWithPickup(
        rateTables,
        weightOz,
        zone,
        [carrier],
        carrierEligibility,
        dhlPickupCost,
      );

      if (!bestCarrier) continue;

      const baseRate = bestCarrier.rate;

      let quotedRate: number;
      if (strategy === PricingStrategy.Discount) {
        quotedRate = calculateDiscountQuote(merchantRate, amount, baseRate);
      } else {
        quotedRate = calculateSpreadQuote(baseRate, amount);
      }

      const savings = merchantRate - quotedRate;
      const profit = quotedRate - baseRate;

      if (savings > 0) {
        ordersWon += orderCount;
        totalMerchantSavings += savings * orderCount;
        totalRedoProfit += profit * orderCount;
      }
    }
  }

  if (ordersWon === 0) return null;

  const ordersWonPct = totalOrders > 0 ? (ordersWon / totalOrders) * 100 : 0;
  const monthlySavings = totalMerchantSavings / 3;
  const annualSavings = totalMerchantSavings * 4;
  const annualRedoProfit = totalRedoProfit * 4;

  return {
    carrier,
    strategy,
    amount,
    unit:
      strategy === PricingStrategy.Discount
        ? SpreadUnit.Percent
        : SpreadUnit.Dollars,
    ordersWon,
    ordersWonPct,
    totalMerchantSavings,
    totalRedoProfit,
    avgMerchantSavingsPerOrder:
      ordersWon > 0 ? totalMerchantSavings / ordersWon : 0,
    avgRedoProfitPerOrder: ordersWon > 0 ? totalRedoProfit / ordersWon : 0,
    monthlySavings,
    annualSavings,
    annualRedoProfit,
  };
}

function calculateRevShareForCarrier(
  rateTables: RedoCarrierRateTables,
  rateGrid: RateGrid,
  carrier: RateTableCarrier,
  carrierEligibility: FullCarrierEligibility,
  config: OptimizerConfig,
  revShareSettings: RevShareSettings,
): CarrierQuoteResult | null {
  let totalOrders = 0;
  let ordersWon = 0;
  let totalMerchantSavings = 0;
  let totalRedoProfit = 0;

  for (let wi = 0; wi < rateGrid.weightBucketsOz.length; wi++) {
    const weightOz = rateGrid.weightBucketsOz[wi]!;

    for (let zi = 0; zi < rateGrid.zones.length; zi++) {
      const zone = rateGrid.zones[zi]!;
      const merchantRate = rateGrid.priceGrid[wi]?.[zi] ?? null;
      const orderCount = rateGrid.countGrid[wi]?.[zi] ?? 0;

      if (merchantRate === null || orderCount === 0) continue;

      totalOrders += orderCount;

      const carrierRate = getBestCarrierRateWithPickup(
        rateTables,
        weightOz,
        zone,
        [carrier],
        carrierEligibility,
        0,
      );
      if (!carrierRate) continue;

      const savings = merchantRate - carrierRate.rate;
      const revSharePct = getRevSharePercentage(
        carrier,
        weightOz,
        revShareSettings,
      );
      const profit = carrierRate.rate * revSharePct;

      if (savings > 0) {
        ordersWon += orderCount;
        totalMerchantSavings += savings * orderCount;
        totalRedoProfit += profit * orderCount;
      }
    }
  }

  if (ordersWon === 0) return null;

  const ordersWonPct = totalOrders > 0 ? (ordersWon / totalOrders) * 100 : 0;
  const monthlySavings = totalMerchantSavings / 3;
  const annualSavings = totalMerchantSavings * 4;
  const annualRedoProfit = totalRedoProfit * 4;

  return {
    carrier,
    strategy: PricingStrategy.Discount,
    amount: 0,
    unit: SpreadUnit.Percent,
    ordersWon,
    ordersWonPct,
    totalMerchantSavings,
    totalRedoProfit,
    avgMerchantSavingsPerOrder:
      ordersWon > 0 ? totalMerchantSavings / ordersWon : 0,
    avgRedoProfitPerOrder: ordersWon > 0 ? totalRedoProfit / ordersWon : 0,
    monthlySavings,
    annualSavings,
    annualRedoProfit,
  };
}

export const DEFAULT_UPSELL_DISCOUNT_PCT = 3;
export const MIN_DHL_ORDERS_WON_PCT = 80;

export function calculateAllCarrierResults(
  rateTables: RedoCarrierRateTables,
  shipments: HistoricalShipment[],
  carrierEligibility: FullCarrierEligibility,
  config: OptimizerConfig,
  revShareSettings: RevShareSettings = DEFAULT_REV_SHARE_SETTINGS,
  discountPct = DEFAULT_UPSELL_DISCOUNT_PCT,
  daysBack = 90,
): CarrierQuoteResult[] {
  const weightBuckets = extractWeightBucketsOz(rateTables);
  const rateGrid = buildRateGridFromShipments(shipments, weightBuckets);

  const eligibleCarriers = (
    Object.keys(carrierEligibility) as RateTableCarrier[]
  ).filter((c) => carrierEligibility[c]?.eligible);

  const revShareSet = new Set<RateTableCarrier>(REV_SHARE_CARRIERS);
  const results: CarrierQuoteResult[] = [];

  for (const carrier of eligibleCarriers) {
    let result: CarrierQuoteResult | null;
    if (revShareSet.has(carrier)) {
      const settings: RevShareSettings =
        carrier === RateTableCarrier.UPS
          ? {
              ...revShareSettings,
              ups: {
                ...revShareSettings.ups,
                activeRateType: UpsRateType.GroundSaver,
              },
            }
          : revShareSettings;
      result = calculateRevShareForCarrier(
        rateTables,
        rateGrid,
        carrier,
        carrierEligibility,
        config,
        settings,
      );
    } else if (carrier === RateTableCarrier.DHL_ECS) {
      // Pass 1: estimate DHL order count using full volume (underestimates pickup cost)
      const pass1 = calculateQuoteForCarrier(
        rateTables,
        rateGrid,
        carrier,
        carrierEligibility,
        PricingStrategy.Discount,
        discountPct,
        config,
      );
      if (pass1 && pass1.ordersWon > 0) {
        // Pass 2: re-amortize pickup cost over only DHL-won volume for accuracy
        const dhlDailyVolume = pass1.ordersWon / estimateWeekdays(daysBack);
        result = calculateQuoteForCarrier(
          rateTables,
          rateGrid,
          carrier,
          carrierEligibility,
          PricingStrategy.Discount,
          discountPct,
          { ...config, dailyVolume: dhlDailyVolume },
        );
      } else {
        result = pass1;
      }
      // DHL only makes sense as an upsell if it wins the vast majority of orders;
      // otherwise pickup cost amortization stays too high to be compelling.
      if (result && result.ordersWonPct < MIN_DHL_ORDERS_WON_PCT) {
        result = null;
      }
    } else {
      result = calculateQuoteForCarrier(
        rateTables,
        rateGrid,
        carrier,
        carrierEligibility,
        PricingStrategy.Discount,
        discountPct,
        config,
      );
    }
    if (result) results.push(result);
  }

  return results.sort((a, b) => b.annualRedoProfit - a.annualRedoProfit);
}

export function optimizeCarrierPricing(
  rateTables: RedoCarrierRateTables,
  shipments: HistoricalShipment[],
  carrierEligibility: FullCarrierEligibility,
  config: OptimizerConfig,
  revShareSettings: RevShareSettings = DEFAULT_REV_SHARE_SETTINGS,
): OptimizerResult | null {
  const weightBuckets = extractWeightBucketsOz(rateTables);
  const rateGrid = buildRateGridFromShipments(shipments, weightBuckets);

  const { carrier: predominantCarrier, pct: predominantCarrierPct } =
    determinePredominantCarrier(shipments);

  const eligibleCarriers = (
    Object.keys(carrierEligibility) as RateTableCarrier[]
  ).filter((c) => carrierEligibility[c]?.eligible && c !== predominantCarrier);

  if (eligibleCarriers.length === 0) {
    return null;
  }

  const revShareCarrierSet = new Set<RateTableCarrier>(REV_SHARE_CARRIERS);
  const spreadCarrierSet = new Set<RateTableCarrier>(SPREAD_CARRIERS);

  const eligibleRevShare = eligibleCarriers.filter((c) =>
    revShareCarrierSet.has(c),
  );
  const eligibleSpread = eligibleCarriers.filter((c) =>
    spreadCarrierSet.has(c),
  );

  const revShareResults: CarrierQuoteResult[] = [];
  for (const carrier of eligibleRevShare) {
    if (carrier === RateTableCarrier.UPS) {
      const settings: RevShareSettings = {
        ...revShareSettings,
        ups: {
          ...revShareSettings.ups,
          activeRateType: UpsRateType.GroundSaver,
        },
      };
      const result = calculateRevShareForCarrier(
        rateTables,
        rateGrid,
        carrier,
        carrierEligibility,
        config,
        settings,
      );
      if (result) revShareResults.push(result);
    } else {
      const result = calculateRevShareForCarrier(
        rateTables,
        rateGrid,
        carrier,
        carrierEligibility,
        config,
        revShareSettings,
      );
      if (result) revShareResults.push(result);
    }
  }

  const bestRevShare =
    revShareResults.length > 0
      ? revShareResults.reduce((best, curr) =>
          curr.annualRedoProfit > best.annualRedoProfit ? curr : best,
        )
      : null;

  const spreadResults: CarrierQuoteResult[] = [];
  const discountPcts = [5, 10, 15, 20, 25];

  for (const carrier of eligibleSpread) {
    for (const discountPct of discountPcts) {
      const result = calculateQuoteForCarrier(
        rateTables,
        rateGrid,
        carrier,
        carrierEligibility,
        PricingStrategy.Discount,
        discountPct,
        config,
      );
      if (!result) continue;

      if (
        !bestRevShare ||
        result.annualRedoProfit > bestRevShare.annualRedoProfit
      ) {
        spreadResults.push(result);
      }
    }
  }

  const allResults = [...revShareResults, ...spreadResults];
  if (allResults.length === 0) {
    return null;
  }

  allResults.sort((a, b) => b.annualRedoProfit - a.annualRedoProfit);

  const best = allResults[0]!;

  return {
    bestCarrier: best.carrier,
    bestCarrierConfig: {
      strategy: best.strategy,
      amount: best.amount,
      unit: best.unit,
    },
    projectedMonthlySavings: best.monthlySavings,
    projectedAnnualSavings: best.annualSavings,
    projectedAnnualRedoProfit: best.annualRedoProfit,
    ordersWonPct: best.ordersWonPct,
    allCarrierResults: allResults,
    predominantCarrier: predominantCarrier ?? undefined,
    predominantCarrierPct,
  };
}
